import React, { useEffect, useMemo, useState } from 'react';
import { Card, Spin, Alert } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import type { RootState } from '@/store';
import {
  getDashboardMetric21,
  type DashboardMetric21ApplicationScope,
} from '@/services/dashboardService';

import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
} from '@syncfusion/ej2-react-grids';

type Row = DashboardMetric21ApplicationScope;

export type EX1FinancialApplicationsScopeGridProps = {
  height?: number; // card content height
  title?: string;
};

const ScopeChip = ({ inScope }: { inScope: boolean }) => {
  const bg = inScope ? '#DCFCE7' : '#F3F4F6';
  const border = inScope ? '#86EFAC' : '#E5E7EB';
  const color = inScope ? '#16A34A' : '#6B7280';
  const text = inScope ? 'In Scope' : 'Out of Scope';

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '6px 12px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        background: bg,
        border: `1px solid ${border}`,
        color,
        whiteSpace: 'nowrap',
        minWidth: 92,
      }}
    >
      {text}
    </span>
  );
};

export const EX1FinancialApplicationsScopeGrid: React.FC<
  EX1FinancialApplicationsScopeGridProps
> = ({ title = 'Financial Applications' }) => {
  const navigate = useNavigate();

  const { selectedReport } = useSelector(
    (state: RootState) => state.annualReport,
  );
  const annualReportId = selectedReport?.annualReportId;

  const { isAdmin } = useSelector((state: RootState) => state.auth);

  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!annualReportId) {
        setRows([]);
        setLoading(false);
        setErrorMessage(null);
        return;
      }

      setLoading(true);
      setErrorMessage(null);

      try {
        const data = await getDashboardMetric21(annualReportId);
        if (!isMounted) return;
        setRows(Array.isArray(data) ? data : []);
      } catch (err: any) {
        if (!isMounted) return;
        setRows([]);
        setErrorMessage(
          err?.message ||
            err?.Message ||
            'Failed to load Metric 21 (Applications Scope).',
        );
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [annualReportId]);

  const dataSource = useMemo(() => {
    // Optional: In Scope first, then alphabetical
    return [...rows].sort((a, b) => {
      if (a.isInScope !== b.isInScope) return a.isInScope ? -1 : 1;
      return (a.applicationName || '').localeCompare(b.applicationName || '');
    });
  }, [rows]);

  const isEmpty = !loading && !errorMessage && dataSource.length === 0;

  const goToExhibit9Applications = () => {
    if (isAdmin) {
      navigate('/audit-plan/?tab=applications');
    }
  };

  return (
    <Card title={title}>
      <div style={{ position: 'relative' }}>
        {loading ? (
          <div
            style={{
              minHeight: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
            }}
          >
            <Spin />
          </div>
        ) : errorMessage ? (
          <div style={{ padding: 16 }}>
            <Alert
              type='error'
              message='Dashboard Metric 21 Error'
              description={errorMessage}
              showIcon
            />
          </div>
        ) : isEmpty ? (
          <div style={{ padding: 16 }}>
            <Alert
              type='info'
              message='No data'
              description='No applications were returned.'
              showIcon
            />
          </div>
        ) : (
          <GridComponent
            dataSource={dataSource}
            height='auto' // key change
            rowHeight={44}
            allowSorting={false}
            allowPaging={false}
            enableHover={true}
            selectionSettings={{ type: 'Single' }}
            rowSelected={() => goToExhibit9Applications()}
          >
            <ColumnsDirective>
              <ColumnDirective
                field='applicationName'
                headerText='Application Name'
                width={650} // key change (pixels)
                textAlign='Left'
                clipMode='EllipsisWithTooltip'
              />
              <ColumnDirective
                headerText='Scope Status'
                width={220} // key change (pixels)
                textAlign='Right'
                template={(props: Row) => (
                  <ScopeChip inScope={!!props.isInScope} />
                )}
              />
            </ColumnsDirective>
          </GridComponent>
        )}
      </div>
    </Card>
  );
};

export default EX1FinancialApplicationsScopeGrid;
